import React, { useState, useEffect } from "react";
import { CheckCircle2, Circle, NotebookPen, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/kg/Markdown";
import { EntryComposer } from "@/components/journey/EntryComposer";

export function NodeFields({ bodyFields, data }) {
  return (
    <div className="space-y-5">
      {bodyFields.map((f) => (
        <div key={f.key}>
          <div className="text-[11px] uppercase tracking-wide text-[#14140f]/45 font-semibold mb-1">
            {f.label}
          </div>
          {Array.isArray(data[f.key]) ? (
            <div className="flex flex-wrap gap-2">
              {data[f.key].map((v, idx) => (
                <span key={`${f.key}-${idx}`} className="text-sm px-2.5 py-1 rounded-full bg-[#f5f4f0] text-[#14140f]/80">
                  {v}
                </span>
              ))}
            </div>
          ) : f.type === "markdown" ? (
            <Markdown className="text-[#14140f]/90">{data[f.key]}</Markdown>
          ) : (
            <p className="text-[15px] text-[#14140f]/90 leading-7 whitespace-pre-line">{data[f.key]}</p>
          )}
        </div>
      ))}
      {bodyFields.length === 0 && (
        <p className="text-[#14140f]/45 italic">No further detail recorded yet.</p>
      )}
    </div>
  );
}

export function RelationshipList({ outgoing, incoming, onJumpTo }) {
  if (outgoing.length === 0 && incoming.length === 0) return null;
  return (
    <div className="mt-8 pt-6 border-t border-[#14140f]/10">
      <div className="text-[11px] uppercase tracking-wide text-[#14140f]/45 font-semibold mb-3">
        Relationships — tap to explore
      </div>
      <div className="flex flex-col gap-2">
        {outgoing.map((e) => (
          <button
            key={e.id}
            onClick={() => onJumpTo(e.target_id)}
            data-testid={`detail-rel-out-${e.id}`}
            className="group flex items-center gap-2 text-left text-sm bg-[#f5f4f0]/50 hover:bg-[#f5f4f0] border border-[#14140f]/10 rounded-md px-3 py-2 transition-colors"
          >
            <ArrowUpRight className="w-4 h-4 text-[#c23c28] shrink-0" />
            <span className="text-[#14140f]/55">{e.label}</span>
            {e.props?.stance && <span className="text-[11px] text-[#0a7d5f]">[{e.props.stance}]</span>}
            <span className="font-medium text-[#14140f] group-hover:text-[#c23c28]">{e.target_label}</span>
          </button>
        ))}
        {incoming.map((e) => (
          <button
            key={e.id}
            onClick={() => onJumpTo(e.source_id)}
            data-testid={`detail-rel-in-${e.id}`}
            className="group flex items-center gap-2 text-left text-sm bg-[#f5f4f0]/50 hover:bg-[#f5f4f0] border border-[#14140f]/10 rounded-md px-3 py-2 transition-colors"
          >
            <ArrowDownLeft className="w-4 h-4 text-[#a86400] shrink-0" />
            <span className="font-medium text-[#14140f] group-hover:text-[#c23c28]">{e.source_label}</span>
            <span className="text-[#14140f]/55">{e.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function LearnerActions({ node, learned, onToggleLearned }) {
  const { user } = useAuth();
  const [noteOpen, setNoteOpen] = useState(false);

  useEffect(() => {
    setNoteOpen(false);
  }, [node.id]);

  const saveEntry = async (payload) => {
    try {
      await api.post("/notes", { ...payload, node_id: node.id });
      toast.success("Saved to your journal");
      setNoteOpen(false);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Please sign in to take notes" : (e.response?.data?.detail || "Could not save note"));
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-[#14140f]/10">
        {user ? (
          <>
            <Button
              variant={learned ? "default" : "outline"}
              onClick={() => onToggleLearned(node.id)}
              data-testid="detail-toggle-learned"
              className={learned ? "bg-[#ff7360] hover:bg-[#e85a46] text-[#14140f]" : ""}
            >
              {learned ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Circle className="w-4 h-4 mr-2" />}
              {learned ? "Learned" : "Mark as learned"}
            </Button>
            <Button variant="outline" onClick={() => setNoteOpen((v) => !v)} data-testid="detail-add-note">
              <NotebookPen className="w-4 h-4 mr-2" /> Add a note
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={login} data-testid="detail-signin-to-track">
            Sign in to track &amp; take notes
          </Button>
        )}
      </div>

      {noteOpen && user && (
        <div className="mt-4">
          <EntryComposer
            target={{ node_id: node.id, title: node.label }}
            allowClear={false}
            onAdd={saveEntry}
          />
        </div>
      )}
    </>
  );
}
